'use client'

import MicIcon from '@mui/icons-material/Mic'
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined'
import { Alert, IconButton, Snackbar, Tooltip, alpha } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type RecognitionError =
    | 'not-allowed'
    | 'audio-capture'
    | 'network'
    | 'no-speech'
    | 'aborted'
    | 'unsupported'
    | 'unknown'

interface BrowserSpeechRecognitionAlternative {
    transcript: string
}

interface BrowserSpeechRecognitionResult {
    isFinal: boolean
    0: BrowserSpeechRecognitionAlternative
}

interface BrowserSpeechRecognitionResultList {
    length: number
    [index: number]: BrowserSpeechRecognitionResult
}

interface BrowserSpeechRecognitionEvent {
    resultIndex: number
    results: BrowserSpeechRecognitionResultList
}

interface BrowserSpeechRecognitionErrorEvent {
    error?: string
}

interface BrowserSpeechRecognition {
    continuous: boolean
    interimResults: boolean
    lang: string
    onresult: ((event: BrowserSpeechRecognitionEvent) => void) | null
    onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null
    onend: (() => void) | null
    start: () => void
    stop: () => void
}

interface BrowserSpeechRecognitionConstructor {
    new(): BrowserSpeechRecognition
}

interface VoiceInputButtonProps {
    locale: string
    disabled?: boolean
    size?: 'small' | 'medium'
    onTranscript: (transcript: string) => void
}

interface VoiceTooltipCopy {
    unsupported: string
    listening: string
    permissionDenied: string
    microphoneMissing: string
    networkIssue: string
    noSpeech: string
    stopped: string
    genericError: string
    idle: string
    insecureContext: string
}

const resolveRecognitionLanguage = (locale: string): string => {
    const normalized = locale.toLowerCase()
    if (normalized.startsWith('ar')) return 'ar-SA'
    if (normalized.startsWith('en')) return 'en-US'
    return locale
}

const mapRecognitionError = (error?: string): RecognitionError => {
    if (error === 'not-allowed') return 'not-allowed'
    if (error === 'audio-capture') return 'audio-capture'
    if (error === 'network') return 'network'
    if (error === 'no-speech') return 'no-speech'
    if (error === 'aborted') return 'aborted'
    return 'unknown'
}

const getTooltipCopy = (locale: string): VoiceTooltipCopy => {
    const isArabic = locale.toLowerCase().startsWith('ar')
    if (isArabic) {
        return {
            unsupported: 'الإدخال الصوتي غير مدعوم في هذا المتصفح',
            listening: 'يتم الاستماع... اضغط للإيقاف',
            permissionDenied: 'تم رفض إذن الميكروفون',
            microphoneMissing: 'لم يتم العثور على ميكروفون',
            networkIssue: 'مشكلة في الشبكة للتعرف الصوتي',
            noSpeech: 'لم يتم اكتشاف صوت، حاول مرة أخرى',
            stopped: 'تم إيقاف الإدخال الصوتي',
            genericError: 'تعذر بدء الإدخال الصوتي',
            idle: 'استخدم الإدخال الصوتي',
            insecureContext: 'الإدخال الصوتي يحتاج اتصالا آمنا (HTTPS أو localhost)'
        }
    }

    return {
        unsupported: 'Voice input is not supported in this browser',
        listening: 'Listening... tap to stop',
        permissionDenied: 'Microphone permission denied',
        microphoneMissing: 'Microphone not detected',
        networkIssue: 'Voice recognition network issue',
        noSpeech: 'No speech detected, try again',
        stopped: 'Voice input stopped',
        genericError: 'Unable to start voice input',
        idle: 'Use voice input',
        insecureContext: 'Voice input needs a secure connection (HTTPS or localhost)'
    }
}

const getTooltipLabel = (
    isSupported: boolean,
    isListening: boolean,
    lastError: RecognitionError | null,
    copy: VoiceTooltipCopy
): string => {
    if (!isSupported) return copy.unsupported
    if (isListening) return copy.listening
    if (lastError === 'not-allowed') return copy.permissionDenied
    if (lastError === 'audio-capture') return copy.microphoneMissing
    if (lastError === 'network') return copy.networkIssue
    if (lastError === 'no-speech') return copy.noSpeech
    if (lastError === 'aborted') return copy.stopped
    if (lastError === 'unknown') return copy.genericError
    return copy.idle
}

export const VoiceInputButton = ({ locale, disabled = false, size = 'small', onTranscript }: VoiceInputButtonProps) => {
    const recognitionRef = useRef<BrowserSpeechRecognition | null>(null)
    const [isSupported, setIsSupported] = useState(true)
    const [isListening, setIsListening] = useState(false)
    const [lastError, setLastError] = useState<RecognitionError | null>(null)
    const [toastMessage, setToastMessage] = useState<string | null>(null)
    const recognitionLanguage = useMemo(() => resolveRecognitionLanguage(locale), [locale])
    const tooltipCopy = useMemo(() => getTooltipCopy(locale), [locale])

    const stopRecognition = useCallback(() => {
        recognitionRef.current?.stop()
    }, [])

    useEffect(() => {
        if (typeof window === 'undefined') return
        if (!window.isSecureContext) {
            setIsSupported(false)
            setLastError('unsupported')
            return
        }

        const SpeechRecognitionCtor = (
            window as Window & {
                webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor
                SpeechRecognition?: BrowserSpeechRecognitionConstructor
            }
        ).SpeechRecognition || (
            window as Window & {
                webkitSpeechRecognition?: BrowserSpeechRecognitionConstructor
            }
        ).webkitSpeechRecognition

        if (!SpeechRecognitionCtor) {
            setIsSupported(false)
            setLastError('unsupported')
            return
        }

        const recognition = new SpeechRecognitionCtor()
        recognition.continuous = false
        recognition.interimResults = true
        recognition.lang = recognitionLanguage

        recognition.onresult = event => {
            const transcriptParts: string[] = []

            for (let index = event.resultIndex; index < event.results.length; index += 1) {
                const result = event.results[index]
                if (!result?.isFinal) continue

                const piece = result[0]?.transcript?.trim()
                if (piece) transcriptParts.push(piece)
            }

            const finalTranscript = transcriptParts.join(' ').trim()

            if (!finalTranscript) return
            onTranscript(finalTranscript)
        }

        recognition.onerror = event => {
            const mappedError = mapRecognitionError(event.error)
            setLastError(mappedError)
            setToastMessage(getTooltipLabel(true, false, mappedError, tooltipCopy))
            setIsListening(false)
        }

        recognition.onend = () => {
            setIsListening(false)
        }

        recognitionRef.current = recognition

        return () => {
            recognition.stop()
            recognitionRef.current = null
        }
    }, [onTranscript, recognitionLanguage, tooltipCopy])

    const handleMicClick = () => {
        if (disabled) return
        if (!isSupported) {
            setToastMessage(window.isSecureContext ? tooltipCopy.unsupported : tooltipCopy.insecureContext)
            return
        }
        if (isListening) {
            stopRecognition()
            return
        }

        setLastError(null)
        try {
            recognitionRef.current?.start()
            setIsListening(true)
        } catch {
            setLastError('unknown')
            setToastMessage(tooltipCopy.genericError)
            setIsListening(false)
        }
    }

    const tooltipLabel = getTooltipLabel(isSupported, isListening, lastError, tooltipCopy)

    return (
        <>
            <Tooltip title={tooltipLabel}>
                <span>
                    <IconButton
                        size={size}
                        aria-label={tooltipLabel}
                        onClick={handleMicClick}
                        disabled={disabled}
                        sx={theme => ({
                            color: isListening ? theme.palette.error.main : alpha(theme.palette.text.primary, 0.68),
                            bgcolor: isListening ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                            animation: isListening ? 'voicePulse 1.2s ease-in-out infinite' : 'none',
                            '@keyframes voicePulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.08)' },
                                '100%': { transform: 'scale(1)' }
                            },
                            '&:hover': {
                                bgcolor: isListening
                                    ? alpha(theme.palette.error.main, 0.15)
                                    : alpha(theme.palette.primary.main, 0.08)
                            }
                        })}
                    >
                        {isListening ? <MicIcon sx={{ fontSize: 22 }} /> : <MicNoneOutlinedIcon sx={{ fontSize: 22 }} />}
                    </IconButton>
                </span>
            </Tooltip>
            <Snackbar
                open={Boolean(toastMessage)}
                autoHideDuration={2600}
                onClose={() => setToastMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="info" variant="filled" onClose={() => setToastMessage(null)} sx={{ width: '100%' }}>
                    {toastMessage}
                </Alert>
            </Snackbar>
        </>
    )
}
